import { createContext, useContext, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { AnimalRow, ProcedureRow } from '@/services/animal-management';

export type ActiveAnimalGrid =
  | 'animals'
  | 'procedures'
  | 'shipping-integration';

export type AnimalManagementContextType = {
  activeGrid: ActiveAnimalGrid;
  selectedAnimals: AnimalRow[];
  selectedProcedures: ProcedureRow[];
  setActiveGrid: (grid: ActiveAnimalGrid) => void;
  setSelectedAnimals: (rows: AnimalRow[]) => void;
  setSelectedProcedures: (rows: ProcedureRow[]) => void;
};

// #39990: the selection doubles as the shipping cart, so keep it across a
// page refresh (per browser tab).
const SELECTED_ANIMALS_STORAGE_KEY =
  'animal-management-selected-animals';

const readSelectedAnimals = (): AnimalRow[] => {
  try {
    const stored = sessionStorage.getItem(SELECTED_ANIMALS_STORAGE_KEY);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? (parsed as AnimalRow[]) : [];
  } catch {
    return [];
  }
};

const writeSelectedAnimals = (rows: AnimalRow[]) => {
  try {
    if (rows.length)
      sessionStorage.setItem(
        SELECTED_ANIMALS_STORAGE_KEY,
        JSON.stringify(rows),
      );
    else sessionStorage.removeItem(SELECTED_ANIMALS_STORAGE_KEY);
  } catch {
    // Storage full or unavailable: the selection still works for this page.
  }
};

const AnimalManagementContext = createContext<
  AnimalManagementContextType | undefined
>(undefined);

export function AnimalManagementProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const activeGrid: ActiveAnimalGrid =
    tab === 'procedures' || tab === 'shipping-integration' ? tab : 'animals';
  const setActiveGrid = (grid: ActiveAnimalGrid) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', grid);
        return next;
      },
      { replace: true },
    );
  };
  const [selectedAnimals, setSelectedAnimalsState] =
    useState<AnimalRow[]>(readSelectedAnimals);
  const setSelectedAnimals = (rows: AnimalRow[]) => {
    setSelectedAnimalsState(rows);
    writeSelectedAnimals(rows);
  };
  const [selectedProcedures, setSelectedProcedures] = useState<ProcedureRow[]>(
    [],
  );

  return (
    <AnimalManagementContext.Provider
      value={{
        activeGrid,
        selectedAnimals,
        selectedProcedures,
        setActiveGrid,
        setSelectedAnimals,
        setSelectedProcedures,
      }}
    >
      {children}
    </AnimalManagementContext.Provider>
  );
}

export function useAnimalManagementContext() {
  const context = useContext(AnimalManagementContext);

  if (!context) {
    throw new Error(
      'useAnimalManagementContext must be used within AnimalManagementProvider',
    );
  }

  return context;
}
